import * as React from "react";
import { Form } from "formik";
import {
    Breakpoint,
    SelectableButtonItem,
    useAsyncEffect,
    useRelodeAppUI,
    wizardHelpers,
} from "@relode/core";

import { useAddJobContext } from "../_infra/addJobProvider";
import { addJobSchema } from "../_infra/validationSchema";
import { AddJobSeedData, AddJobStepsEnum } from "../types";
import { AddJobDesktop } from "./addJobDesktop";
import { AddJobMobile } from "./addJobMobile";
import { JobManagerContact } from "apps/client/src/shared/types";

interface AddJobWrapperProps {
    handleClose: () => void;
    onSaveAndExit: (jobId: number) => void;
    onFinish: (
        jobId: number,
        jobName: string,
        jobManagerContact: JobManagerContact
    ) => void;
    selectedJobId?: number;
    isEditJob: boolean;
    seedData?: AddJobSeedData;
}

export const AddJobWrapper = (props: AddJobWrapperProps) => {
    const {
        handleClose,
        onSaveAndExit,
        selectedJobId,
        onFinish,
        isEditJob,
        seedData,
    } = props;
    const { ui: appUIState } = useRelodeAppUI();
    const {
        jobSteps,
        actions,
        formikProps,
        setFieldValue,
        state: {
            ui: { activeStepId, showOrderConfirmationStep },
            data: { jobId, jobManagerDetails },
        },
    } = useAddJobContext();

    useAsyncEffect(async () => {
        let pageTitle = "Add Job - ";

        if (isEditJob && selectedJobId) {
            pageTitle = "Edit Job - ";
        }
        switch (activeStepId) {
            case AddJobStepsEnum.Step1: {
                if (isEditJob && selectedJobId) {
                    await actions.loadAddJobNewStep1(selectedJobId);
                    appUIState.actions.setPageTitle(`${pageTitle}Step 1`);
                } else if (selectedJobId && !jobId) {
                    await actions.copyJob(selectedJobId);
                    appUIState.actions.setPageTitle(`${pageTitle}Step 1`);
                } else {
                    await actions.loadAddJobNewStep1(
                        jobId ? jobId : 0,
                        seedData
                    );
                    appUIState.actions.setPageTitle(`${pageTitle}Step 1`);
                }
                break;
            }
            case AddJobStepsEnum.Step2: {
                await actions.loadAddJobStep2(jobId);
                appUIState.actions.setPageTitle(`${pageTitle}Step 2`);
                break;
            }
            case AddJobStepsEnum.Step3: {
                await actions.loadAddJobStep3(jobId);
                appUIState.actions.setPageTitle(`${pageTitle}Step 3`);
                break;
            }
            case AddJobStepsEnum.Exit: {
                onSaveAndExit(jobId);
                break;
            }
            case AddJobStepsEnum.Finish: {
                onFinish(jobId, jobSteps.stepTwo.jobName, jobManagerDetails);
                break;
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStepId]);

    const isStepValid = async (stepName: string): Promise<boolean> => {
        if (stepName === AddJobStepsEnum.Step3) {
            return true;
        }
        return await wizardHelpers.checkStepValidity({
            stepName,
            schema: addJobSchema,
            formik: formikProps,
            values: jobSteps,
        });
    };

    const isCurrentStepFilthy = (stepName: string) => {
        const keys = Object.keys(jobSteps[stepName]);
        return !keys.every(
            key =>
                jobSteps[stepName][key] ===
                formikProps.initialValues[stepName][key]
        );
    };

    const handleNext = async () => {
        const isValid = await isStepValid(activeStepId);

        if (isValid) {
            if (
                activeStepId === AddJobStepsEnum.Step2 &&
                !showOrderConfirmationStep
            ) {
                await actions.moveJobWithoutPlanToRequested(
                    jobId,
                    jobSteps.stepTwo
                );
            } else if (activeStepId === AddJobStepsEnum.Step3) {
                await actions.moveJobWithPlanToRequested(
                    jobId,
                    jobSteps.stepThree.jobConcurrencyToken
                );
            } else {
                await actions.handleNextStep(activeStepId, jobId, jobSteps);
            }
        }
    };

    const handlePrevious = async () => {
        await actions.handlePreviousStep(activeStepId, jobId, jobSteps, false);
    };

    const handleSaveAndExit = async () => {
        const isValid = await isStepValid(activeStepId);
        if (isValid) {
            await actions.handleSaveAndExit(activeStepId, jobId, jobSteps);
        }
    };

    const handleFieldChange = (field: string) => (e: any) => {
        setFieldValue(e.target.value, field);
    };

    const handleButtonFieldChange = (field: string) => (
        item: SelectableButtonItem
    ) => {
        setFieldValue(item.id, field);
    };

    return (
        <Form>
            <Breakpoint mobile>
                <AddJobMobile
                    handleClose={handleClose}
                    handleNext={handleNext}
                    handlePrevious={handlePrevious}
                    handleSaveAndExit={handleSaveAndExit}
                    activeStepId={activeStepId}
                    handleFieldChange={handleFieldChange}
                    handleButtonFieldChange={handleButtonFieldChange}
                />
            </Breakpoint>
            <Breakpoint desktop>
                <AddJobDesktop
                    handleClose={handleClose}
                    handleNext={handleNext}
                    handlePrevious={handlePrevious}
                    handleSaveAndExit={handleSaveAndExit}
                    activeStepId={activeStepId}
                    handleFieldChange={handleFieldChange}
                    handleButtonFieldChange={handleButtonFieldChange}
                />
            </Breakpoint>
        </Form>
    );
};
